import { isToday } from 'date-fns';
import type { ReactNode } from 'react';
import { Col, Empty, InfinityLoading, Loading, Row, Space, Text } from 'ui';
import { useGroupedEvents, useScrollToTodayGroup } from './hooks';

type P<T extends { id: string; startTime: Date }> = {
  createItem: (event: T) => ReactNode;
  events: T[];
  getPathToEventDetail: (id: string) => string;
  hideGroupLabel?: boolean;
  query: {
    fetchNextPage: () => void;
    hasNextPage?: boolean;
    isFetchingNextPage: boolean;
    isRefetching: boolean;
  };
  shouldShowTodayEmptyPlaceholder?: boolean;
};

const EventsListGroup = <T extends { id: string; startTime: Date }>({
  createItem,
  events,
  hideGroupLabel,
  query: { fetchNextPage, hasNextPage, isFetchingNextPage, isRefetching },
  shouldShowTodayEmptyPlaceholder,
}: P<T>) => {
  const groupedEvents = useGroupedEvents<T>({ events, shouldShowTodayEmptyPlaceholder });
  const todayGroupRef = useScrollToTodayGroup();

  return (
    <>
      {isRefetching ? <Loading /> : null}
      {groupedEvents.map((groupedEvent) => (
        <div key={groupedEvent.label} ref={isToday(groupedEvent.date) ? todayGroupRef : null}>
          {hideGroupLabel ? null : (
            <Space orientation="horizontal" size="small">
              <Text size="large" type="title">
                {groupedEvent.label}
              </Text>
              {isToday(groupedEvent.date) ? <Text size="medium">Today</Text> : null}
            </Space>
          )}
          <Row className="events-list-group">
            {groupedEvent.isTodayEmptyPlaceholder ? (
              <Col className="events-list-group__empty">
                <Empty
                  description={{ subtitle: 'There are currently no events for today', title: 'No events' }}
                  fitCenter
                  withoutImage
                />
              </Col>
            ) : (
              groupedEvent.events.map((event) => (
                <Col className="events-list-group__item" key={event.id}>
                  {createItem(event)}
                </Col>
              ))
            )}
          </Row>
        </div>
      ))}
      <InfinityLoading hasMoreData={hasNextPage} isLoading={isFetchingNextPage} onEnter={fetchNextPage} />
    </>
  );
};

export default EventsListGroup;
